import { Grid } from '@features/dashboard/widgets/grid';
import { useScreenSize } from '@hooks/useScreenSize';
import { alpha, Box, BoxProps, styled, Typography } from '@mui/material';
import { ColDef, ColGroupDef, DomLayoutType, RowClickedEvent } from 'ag-grid-enterprise';

interface BlotterCardProps<T> {
  title: string;
  data: T[];
  columns: (ColDef<T, any> | ColGroupDef<T>)[];
  colDefOptions?: ColDef<T, any>;
  fullWidth?: boolean;
  domLayout?: DomLayoutType;
  onRowClicked?: (event: RowClickedEvent<T, any>) => void;
}

interface StyledBoxProps extends BoxProps {
  fullWidth?: boolean;
  autoHeight?: boolean;
  minWidth?: string;
}

const defaultColDef = {
  sortable: true,
  filter: false,
  floatingFilter: false,
  resizable: true,
};

const StyledRoundedBox = styled((props: StyledBoxProps) => <Box {...props} />, {
  shouldForwardProp: (propName: string) => propName !== 'fullWidth' && propName !== 'autoHeight' && propName !== 'minWidth',
})(({ theme, fullWidth, minWidth, autoHeight }) => ({
  padding: '10px',
  overflow: 'hidden',
  outline: `1px solid ${alpha(theme.palette.border, 0.1)}`,
  borderRadius: `${theme.shape.borderRadius}px`,
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minHeight: autoHeight ? 'none' : 400,
  minWidth: minWidth || 400,
  ...(fullWidth
    ? {
        width: '100%',
      }
    : { maxWidth: 600 }),
}));

export const BlotterCard = <T,>({ title, data, columns, colDefOptions, fullWidth, domLayout, onRowClicked }: BlotterCardProps<T>) => {
  const { isMobile } = useScreenSize();

  return (
    <StyledRoundedBox fullWidth={fullWidth} autoHeight={domLayout === 'autoHeight'} minWidth={isMobile ? '100%' : undefined}>
      <Typography variant="h6" gutterBottom sx={{ marginBottom: '10px' }}>
        {title}
      </Typography>
      <Grid<T>
        rowData={data}
        defaultColDef={{ ...defaultColDef, ...colDefOptions }}
        enableCellTextSelection
        columnDefs={columns}
        domLayout={domLayout}
        onRowClicked={onRowClicked}
        autoSizeStrategy={{ type: 'fitGridWidth' }}
      />
    </StyledRoundedBox>
  );
};
